import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const BusinessSocialsContext = createContext();

export const useBusinessSocials = () => {
  const context = useContext(BusinessSocialsContext);
  if (!context) {
    throw new Error('useBusinessSocials must be used within a BusinessSocialsProvider');
  }
  return context;
};

export const BusinessSocialsProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';

  // Determine the correct base URL based on login type
  const getBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/business-socials/api/v1/business-socials`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/business-socials/api/v1/business-socials`;
    } else {
      throw new Error('Invalid login type for business socials access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }

    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access business socials');
    }
  }, [loginType]);

  // ------------------------------------------------------------------
  // CREATE
  // ------------------------------------------------------------------

  // Create a new business socials record
  const createBusinessSocial = useCallback(async (socialData) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      if (!socialData.socials_id) {
        throw new Error('socials_id is required');
      }
      if (!socialData.business_registry_number) {
        throw new Error('business_registry_number is required');
      }

      const baseUrl = getBaseUrl();
      const response = await makeAuthenticatedRequest(baseUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(socialData),
      });

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to create business social record';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  // ------------------------------------------------------------------
  // READ
  // ------------------------------------------------------------------

  // Get business social by socials_id
  const getBusinessSocialById = useCallback(async (socialsId) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      if (!socialsId) {
        throw new Error('socials_id is required');
      }

      const baseUrl = getBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(socialsId)}`
      );

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get business social record';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  // Get business social by business_registry_number
  const getBusinessSocialByRegistry = useCallback(async (registryNumber) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      if (!registryNumber) {
        throw new Error('business_registry_number is required');
      }

      const baseUrl = getBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/registry/${encodeURIComponent(registryNumber)}`
      );

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get business social by registry number';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  // ------------------------------------------------------------------
  // UPDATE
  // ------------------------------------------------------------------

  // Update an existing business socials record
  const updateBusinessSocial = useCallback(async (socialsId, updateData) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      if (!socialsId) {
        throw new Error('socials_id is required');
      }

      const baseUrl = getBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(socialsId)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updateData),
        }
      );

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update business social record';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  // Toggle social visibility
  const toggleSocialVisibility = useCallback(async (socialsId, visible) => {
    validateAccess();

    return await updateBusinessSocial(socialsId, {
      social_visible: visible,
    });
  }, [updateBusinessSocial, validateAccess]);

  // ------------------------------------------------------------------
  // DELETE
  // ------------------------------------------------------------------

  // Delete a business socials record
  const deleteBusinessSocial = useCallback(async (socialsId) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      if (!socialsId) {
        throw new Error('socials_id is required');
      }

      const baseUrl = getBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(socialsId)}`,
        {
          method: 'DELETE',
        }
      );

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete business social record';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  // ------------------------------------------------------------------
  // UTILITIES & HELPERS
  // ------------------------------------------------------------------

  // Check if a business already has a socials record
  const businessHasSocials = useCallback(async (registryNumber) => {
    try {
      const result = await getBusinessSocialByRegistry(registryNumber);
      return { exists: true, data: result.data };
    } catch {
      return { exists: false, data: null };
    }
  }, [getBusinessSocialByRegistry]);

  // Format social data for display
  const formatSocialData = useCallback((social) => {
    if (!social) return null;

    const activePlatforms = [];
    if (social.social_instagram) activePlatforms.push('Instagram');
    if (social.social_facebook) activePlatforms.push('Facebook');
    if (social.social_linkedin) activePlatforms.push('LinkedIn');
    if (social.social_x) activePlatforms.push('X');
    if (social.social_website) activePlatforms.push('Website');

    return {
      ...social,
      activePlatforms,
      platformCount: activePlatforms.length,
      isVisible: social.social_visible,
      hasAnySocial: activePlatforms.length > 0,
    };
  }, []);

  // Get current endpoint info for debugging
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: loginType === 'master'
        ? `${API_BASE_URL}/master/business-socials/api/v1/business-socials`
        : `${API_BASE_URL}/admin/business-socials/api/v1/business-socials`,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master'
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
    };
  }, [loginType, API_BASE_URL]);

  const value = {
    // State
    loading,
    error,

    // CRUD Actions
    createBusinessSocial,
    getBusinessSocialById,
    getBusinessSocialByRegistry,
    updateBusinessSocial,
    deleteBusinessSocial,

    // Convenience Actions
    toggleSocialVisibility,

    // Utilities
    businessHasSocials,
    formatSocialData,
    clearError,
    getEndpointInfo,

    // Computed values
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasBusinessSocialsAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  };

  return (
    <BusinessSocialsContext.Provider value={value}>
      {children}
    </BusinessSocialsContext.Provider>
  );
};